import { z } from "zod";

export const assistantModeSchema = z.enum(["ai", "waiting_human", "human"]);
export const assistantPolicySchema = z
  .object({
    enabled: z.boolean(),
    timezone: z.literal("Asia/Bangkok"),
    startMinute: z.number().int().min(0).max(1439),
    endMinute: z.number().int().min(1).max(1440),
    weekdays: z
      .array(z.number().int().min(0).max(6))
      .max(7)
      .refine((v) => new Set(v).size === v.length),
    holidayDates: z.array(z.iso.date()).max(366),
    knowledge: z
      .array(
        z
          .object({
            id: z.string().regex(/^[a-z0-9-]{1,60}$/),
            locale: z.enum(["ko", "th", "en"]),
            question: z.string().min(1).max(300),
            answer: z.string().min(1).max(2000),
            source: z.string().min(1).max(300),
          })
          .strict(),
      )
      .max(100),
    version: z.number().int().positive(),
  })
  .strict()
  .refine(
    (v) =>
      v.startMinute < v.endMinute &&
      new Set(v.knowledge.map((k) => `${k.locale}:${k.id}`)).size ===
        v.knowledge.length,
  );
export const assistantStateSchema = z
  .object({
    conversationId: z.uuid(),
    mode: assistantModeSchema,
    review: z.enum(["pending", "ai_resolved_review", "taken"]),
    version: z.number().int().positive(),
    aiPending: z.boolean(),
    businessOpen: z.boolean(),
    timezone: z.literal("Asia/Bangkok"),
    startMinute: z.number().int(),
    endMinute: z.number().int(),
  })
  .strict();
export const assistantSummarySchema = z
  .object({
    customerRequest: z.string(),
    aiGuidance: z.string(),
    resolution: z.string(),
    outstanding: z.string(),
    nextAction: z.string(),
    attachments: z.array(
      z.object({ id: z.uuid(), name: z.string(), href: z.string() }).strict(),
    ),
    updatedAt: z.string(),
  })
  .strict();
export const assistantReviewSchema = z
  .object({
    state: assistantStateSchema,
    title: z.string(),
    summary: assistantSummarySchema,
  })
  .strict();
export const assistantCommandSchema = z
  .object({ expectedVersion: z.number().int().positive() })
  .strict();
export type AssistantPolicy = z.infer<typeof assistantPolicySchema>;

/** Server-only calculation; the browser receives the result, never supplies the clock. */
export function isBusinessOpen(policy: AssistantPolicy, now: Date): boolean {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: policy.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const value = (key: string) => parts.find((p) => p.type === key)?.value ?? "";
  const day = `${value("year")}-${value("month")}-${value("day")}`;
  const minute = Number(value("hour")) * 60 + Number(value("minute"));
  return (
    policy.weekdays.includes(new Date(`${day}T00:00:00Z`).getUTCDay()) &&
    !policy.holidayDates.includes(day) &&
    minute >= policy.startMinute &&
    minute < policy.endMinute
  );
}
